// 主入口文件 - 初始化应用
import '@/styles/main.css';
import { authStore } from '@shared/stores/auth-store';
import { notificationService } from '@shared/services/notification-service';
import { httpClient } from '@shared/api/http-client';
import mobileLayoutManager from '@shared/services/mobile-layout-manager';
import { initializeI18n, t } from '@shared/i18n';
import type { User } from '@shared/types/auth';

/**
 * 应用程序主类
 * 负责初始化全局功能和状态管理
 */
class EasyAussieApp {
  private isInitialized = false;

  constructor() {
    initializeI18n();
    this.init();
  }

  /**
   * 初始化应用
   */
  private async init(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // 确保页面可以滚动（修复移动端滚动问题）
      this.ensurePageScrollable();

      // 等待 DOM 准备就绪
      await this.waitForDOM();

      const shouldShowPageLoader = this.shouldShowPageLoader();
      if (shouldShowPageLoader) {
        mobileLayoutManager.showPageLoader();
      }

      // 初始化全局服务
      this.initializeServices();

      // 初始化页面级交互
      await this.initializePageFeatures();

      // 初始化布局组件
      await this.initializeLayout();

      // 初始化权限控制
      this.initializePermissions();

      // 标记为已初始化
      this.isInitialized = true;

      if (shouldShowPageLoader) {
        setTimeout(() => {
          mobileLayoutManager.hidePageLoader();
        }, 800);
      }
    } catch (error) {
      console.error('❌ Failed to initialize app:', error);
      mobileLayoutManager.hidePageLoader();
      notificationService.error(t('app.initFailed'));
    }
  }

  /**
   * 等待 DOM 准备就绪
   */
  private waitForDOM(): Promise<void> {
    return new Promise(resolve => {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => resolve());
      } else {
        resolve();
      }
    });
  }

  private shouldShowPageLoader(): boolean {
    const body = document.body;
    const requiredRoles = body.dataset.requiredRoles || body.dataset.requiredRole || '';
    const authMode = body.dataset.auth || (requiredRoles.trim() ? 'required' : 'public');

    return authMode === 'required' || requiredRoles.trim().length > 0;
  }

  /**
   * 确保页面可以滚动
   */
  private ensurePageScrollable(): void {
    // 移除可能阻止滚动的样式
    document.body.style.overflow = '';
    document.body.classList.remove('overflow-hidden');

    // 确保 html 和 body 可以滚动
    document.documentElement.style.overflow = '';
    document.documentElement.classList.remove('overflow-hidden');
  }

  /**
   * 初始化全局服务
   */
  private initializeServices(): void {
    // 将服务挂载到全局对象，供模板使用
    window.authStore = authStore;
    window.notificationService = notificationService;
    window.httpClient = httpClient;
    window.mobileLayoutManager = mobileLayoutManager;

    // 设置全局错误处理
    this.setupGlobalErrorHandling();
  }

  /**
   * 初始化布局组件
   */
  private async initializeLayout(): Promise<void> {
    try {
      // 获取用户信息
      const user = await authStore.fetchUserProfile();

      // 初始化移动端组件
      await this.initializeMobileComponents();

      // 更新用户状态显示
      this.updateUserInterface(user);
    } catch (error) {
      console.warn('Failed to initialize layout:', error);
    }
  }

  /**
   * 初始化移动端组件
   */
  private async initializeMobileComponents(): Promise<void> {
    try {
      // 动态导入组件类
      const { MobileHeaderComponent } = await import('@app/layout/mobile-header');
      const { MobileFooterComponent } = await import('@app/layout/mobile-footer');

      const body = document.body;
      const headerContainerId = body.dataset.headerContainer || 'header';
      const footerContainerId = body.dataset.footerContainer || 'footer';
      const headerContainer = document.getElementById(headerContainerId);
      const footerContainer = document.getElementById(footerContainerId);

      if (headerContainer) {
        new MobileHeaderComponent({
          containerId: headerContainerId,
          overlayHero: body.dataset.headerOverlay === 'true',
        });
      }

      if (footerContainer) {
        new MobileFooterComponent(footerContainerId);
      }
    } catch (error) {
      console.warn('Failed to initialize mobile components:', error);
    }
  }

  private async initializePageFeatures(): Promise<void> {
    if (document.body.dataset.homeInteractions !== 'true') {
      return;
    }

    try {
      const { initHomeInteractions } = await import('@features/home/home-interactions');
      initHomeInteractions();
    } catch (error) {
      console.warn('Failed to initialize page interactions:', error);
    }
  }

  /**
   * 更新用户界面
   */
  private updateUserInterface(_user: User | null): void {
    // 订阅用户状态变化
    authStore.subscribe(currentUser => {
      this.updateHeaderStatus(currentUser);
      this.updateNavigationPermissions(currentUser);
    });
  }

  /**
   * 更新头部状态
   */
  private updateHeaderStatus(user: User | null): void {
    const avatarSection = document.getElementById('userAvatarSection');
    const loginButton = document.getElementById('loginButton');

    if (!avatarSection || !loginButton) return;

    if (user) {
      avatarSection.classList.remove('hidden');
      loginButton.classList.add('hidden');

      // 更新用户邮箱显示
      const emailElements = document.querySelectorAll('.user-email');
      emailElements.forEach(el => {
        el.textContent = user.email;
      });
    } else {
      avatarSection.classList.add('hidden');
      loginButton.classList.remove('hidden');
    }
  }

  /**
   * 更新导航权限
   */
  private updateNavigationPermissions(user: User | null): void {
    document.querySelectorAll('.role-required, .auth-guard').forEach(element => {
      const htmlElement = element as HTMLElement;
      const roles = this.parseRoleList(
        htmlElement.dataset.requiredRoles || htmlElement.dataset.role || ''
      );
      const mode = htmlElement.dataset.roleMode === 'all' ? 'all' : 'any';
      const authRequired = htmlElement.dataset.auth === 'required' || roles.length > 0;
      const hasAuth = !authRequired || !!user;
      const hasPermission = roles.length === 0 || authStore.hasRoles(roles, mode);

      htmlElement.style.display = hasAuth && hasPermission ? '' : 'none';
    });
  }

  /**
   * 初始化权限控制
   */
  private initializePermissions(): void {
    const enforcePageAccess = () => {
      const body = document.body;
      const legacyRequiredRole = body.dataset.requiredRole;
      const requiredRoles = this.parseRoleList(
        body.dataset.requiredRoles || legacyRequiredRole || ''
      );
      const roleMode = body.dataset.roleMode === 'all' ? 'all' : 'any';
      const authMode = body.dataset.auth || (requiredRoles.length > 0 ? 'required' : 'public');

      if (authMode !== 'required' && requiredRoles.length === 0) {
        return;
      }

      if (requiredRoles.length > 0) {
        authStore.requireRoles(requiredRoles, roleMode).catch(() => {
          // 权限检查失败，会自动跳转
        });
      } else {
        authStore.requireAuth().catch(() => {
          // 权限检查失败，会自动跳转
        });
      }
    };

    enforcePageAccess();

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        enforcePageAccess();
      }
    });
  }

  private parseRoleList(value: string): string[] {
    return value
      .split(',')
      .map(role => role.trim())
      .filter(Boolean);
  }

  /**
   * 设置全局错误处理
   */
  private setupGlobalErrorHandling(): void {
    // 捕获未处理的 Promise 错误
    window.addEventListener('unhandledrejection', event => {
      console.error('Unhandled promise rejection:', event.reason);
      notificationService.error(t('app.unexpectedError'));
      event.preventDefault();
    });

    // 捕获 JavaScript 错误
    window.addEventListener('error', event => {
      console.error('Global error:', event.error);
      if (event.error?.message?.includes('Network')) {
        notificationService.error(t('app.networkError'));
      }
    });
  }
}

/**
 * 全局工具函数
 */

// 用户菜单切换
window.toggleUserMenu = function () {
  const user = authStore.getCurrentUser();
  if (!user) return;

  const menu = document.getElementById('userMenuLoggedIn');
  const avatar = document.getElementById('userAvatarSection');
  if (!menu || !avatar) return;

  // 更新邮箱显示
  const emailDiv = menu.querySelector('.text-sm');
  if (emailDiv) {
    emailDiv.innerHTML = `
      <div class="block px-4 py-2 text-gray-600 font-medium">
        <span class="text-blue-600">${user.email}</span>
      </div>
    `;
  }

  // 切换菜单显示
  menu.classList.toggle('hidden');

  // 点击外部关闭菜单
  const closeMenu = (e: Event) => {
    if (!menu.contains(e.target as Node) && !avatar.contains(e.target as Node)) {
      menu.classList.add('hidden');
      document.removeEventListener('click', closeMenu);
    }
  };

  document.removeEventListener('click', closeMenu);
  setTimeout(() => document.addEventListener('click', closeMenu), 0);
};

// 移动端菜单切换
window.toggleMobileMenu = function () {
  const mobileMenu = document.getElementById('mobileMenu');
  const mobileUserSection = document.getElementById('mobileUserSection');
  const user = authStore.getCurrentUser();

  if (!mobileMenu || !mobileUserSection) return;

  const isHidden = mobileMenu.classList.contains('hidden');
  mobileMenu.classList.toggle('hidden');

  // 只在菜单打开时阻止滚动，关闭时立即恢复
  if (isHidden) {
    document.body.classList.add('overflow-hidden');
  } else {
    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';
  }

  if (user) {
    mobileUserSection.innerHTML = `
      <div class="border-t border-blue-500 pt-4 mt-4">
        <div class="block px-4 py-2 text-sm text-white font-medium border-b border-blue-500">
          ${user.email}
        </div>
        <a href="/pages/user/profile.html"
           class="block px-4 py-3 text-white hover:bg-blue-500 transition-colors">
          个人中心
        </a>
        <a href="#"
           data-action="logout"
           class="block px-4 py-3 text-white hover:bg-blue-500 transition-colors">
          退出登录
        </a>
      </div>
    `;

    mobileUserSection.querySelector('[data-action="logout"]')?.addEventListener('click', e => {
      e.preventDefault();
      authStore.logout();
    });
  } else {
    mobileUserSection.innerHTML = `
      <div class="border-t border-blue-500 pt-4 mt-4">
        <a href="/pages/auth/login.html" 
           class="block px-4 py-3 text-white hover:bg-blue-500 transition-colors">
          ${t('dynamic.signInAccount')}
        </a>
      </div>
    `;
  }
};

// 登出函数
window.logout = function () {
  authStore.logout();
};

// 初始化应用
const app = new EasyAussieApp();

// 导出全局实例
export { app };
export default app;
